import React, { useState, useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

import { RequestCustomPlaylistRequestDTO } from '@/core/api/dtos/request/playlist/requestCustom'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useRequestCustomPlaylist } from '@/core/hooks/usePlaylist'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import {
	Form,
	FormField,
	FormItem,
	FormLabel,
	FormControl,
	FormMessage,
} from '@/shared/components/atoms/Form'
import Icon from '@/shared/components/atoms/Icon'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import { RadioGroup, RadioGroupItem } from '@/shared/components/atoms/Radio'
import { Separator } from '@/shared/components/atoms/Separator'
import { Textarea } from '@/shared/components/atoms/TextArea'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from '@/shared/components/molecules/Dialog/dialog'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { extractErrorMessage } from '@/shared/utils/helpers'

interface RequestCustomPlaylistModalProps {
	isOpen: boolean
	onClose: () => void
}

const RequestCustomPlaylistModal: React.FC<RequestCustomPlaylistModalProps> = ({
	isOpen,
	onClose,
}) => {
	const { t } = useTranslation('common')
	const { showNotification } = useNotificationContext()
	const [selectedType, setSelectedType] = useState<'common' | 'event'>('common')
	const [referenceLinks, setReferenceLinks] = useState<string[]>([''])

	// Validation schema - URL fields are optional but must be valid URLs if provided
	const urlField = yup
		.string()
		.notRequired()
		.test('is-url-or-empty', 'Must be a valid URL', (value) => {
			if (!value || value.trim() === '') return true
			try {
				new URL(value)
				return true
			} catch {
				return false
			}
		})

	const validationSchema = yup.object({
		selected_type: yup.string().oneOf(['common', 'event']).required(),
		event_details: yup.string().max(5000, 'Max 5000 characters').notRequired(),
		song_reference_1: urlField,
		song_reference_2: urlField,
		song_reference_3: urlField,
		song_reference_4: urlField,
		song_reference_5: urlField,
		spotify_url: urlField,
		playlist_assignment: yup.string().oneOf(['self_assign']).required(),
	}) as yup.ObjectSchema<RequestCustomPlaylistRequestDTO>

	const form = useForm<RequestCustomPlaylistRequestDTO>({
		resolver: yupResolver(validationSchema),
		defaultValues: {
			selected_type: 'common',
			event_details: '',
			song_reference_1: '',
			song_reference_2: '',
			song_reference_3: '',
			song_reference_4: '',
			song_reference_5: '',
			spotify_url: '',
			playlist_assignment: 'self_assign',
		},
	})

	const { mutate: requestCustomPlaylist, isPending } = useRequestCustomPlaylist(
		{
			config: useAccountConfig({}),
			options: {
				onSuccess: () => {
					showNotification({
						type: 'success',
						message: t('entities.music.request_custom_playlist_success'),
					})
					form.reset()
					setReferenceLinks([''])
					setSelectedType('common')
					onClose()
				},
				onError: (error: unknown) => {
					const errorMessage = extractErrorMessage(
						error,
						t('entities.music.request_custom_playlist_error_description')
					)
					showNotification({
						type: 'error',
						message: t('entities.music.request_custom_playlist_error'),
						description: errorMessage,
					})
				},
			},
		}
	)

	const handleSubmit = (data: RequestCustomPlaylistRequestDTO) => {
		// Clean up empty strings and convert to undefined
		const payload: RequestCustomPlaylistRequestDTO = {
			selected_type: data.selected_type,
			event_details:
				data.event_details && data.event_details.trim()
					? data.event_details.trim()
					: undefined,
			song_reference_1:
				data.song_reference_1 && data.song_reference_1.trim()
					? data.song_reference_1.trim()
					: undefined,
			song_reference_2:
				data.song_reference_2 && data.song_reference_2.trim()
					? data.song_reference_2.trim()
					: undefined,
			song_reference_3:
				data.song_reference_3 && data.song_reference_3.trim()
					? data.song_reference_3.trim()
					: undefined,
			song_reference_4:
				data.song_reference_4 && data.song_reference_4.trim()
					? data.song_reference_4.trim()
					: undefined,
			song_reference_5:
				data.song_reference_5 && data.song_reference_5.trim()
					? data.song_reference_5.trim()
					: undefined,
			spotify_url:
				data.spotify_url && data.spotify_url.trim()
					? data.spotify_url.trim()
					: undefined,
			playlist_assignment: 'self_assign',
		}

		requestCustomPlaylist(payload)
	}

	const handleAddReferenceLink = () => {
		if (referenceLinks.length < 5) {
			const newLinks = [...referenceLinks, '']
			setReferenceLinks(newLinks)
			// Initialize the new form field
			const fieldName =
				`song_reference_${newLinks.length}` as keyof RequestCustomPlaylistRequestDTO
			form.setValue(fieldName as any, '')
		}
	}

	const handleReferenceLinkChange = (index: number, value: string) => {
		const updatedLinks = [...referenceLinks]
		updatedLinks[index] = value
		setReferenceLinks(updatedLinks)

		// Update form values
		const fieldName =
			`song_reference_${index + 1}` as keyof RequestCustomPlaylistRequestDTO
		form.setValue(fieldName as any, value)
	}

	const handleTypeChange = (value: string) => {
		const newType = value as 'common' | 'event'
		setSelectedType(newType)
		form.setValue('selected_type', newType)
	}

	// Reset form when modal closes
	useEffect(() => {
		if (!isOpen) {
			form.reset()
			setReferenceLinks([''])
			setSelectedType('common')
		}
	}, [isOpen, form])

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="max-w-md w-full max-h-[90vh] overflow-y-auto border border-border">
				<DialogHeader>
					<DialogTitle className="text-xl font-bold text-foreground">
						{t('entities.music.request_custom_playlist')}
					</DialogTitle>
				</DialogHeader>

				<Separator />

				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(handleSubmit)}
						className="space-y-6"
					>
						{/* Playlist Type Selection */}
						<FormField
							control={form.control}
							name="selected_type"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<RadioGroup
											value={field.value}
											onValueChange={(value) => {
												field.onChange(value)
												handleTypeChange(value)
											}}
											className="flex flex-row gap-6"
										>
											<div className="flex items-center space-x-3">
												<div className="relative">
													<RadioGroupItem
														value="common"
														id="common"
														className="size-5 border-2 border-primary bg-transparent data-[state=checked]:border-primary data-[state=checked]:bg-primary dark:border-white dark:bg-transparent dark:data-[state=checked]:border-white dark:data-[state=checked]:bg-transparent [&>span>svg]:fill-white [&>span>svg]:size-2.5"
													/>
												</div>
												<Label
													htmlFor="common"
													className={`cursor-pointer ${
														field.value === 'common'
															? 'text-foreground'
															: 'text-muted-foreground'
													}`}
												>
													{t('entities.music.regular_playlist')}
												</Label>
											</div>
											<div className="flex items-center space-x-3">
												<div className="relative">
													<RadioGroupItem
														value="event"
														id="event"
														className="size-5 border-2 border-primary bg-transparent data-[state=checked]:border-primary data-[state=checked]:bg-primary dark:border-white dark:bg-transparent dark:data-[state=checked]:border-white dark:data-[state=checked]:bg-transparent [&>span>svg]:fill-white [&>span>svg]:size-2.5"
													/>
												</div>
												<Label
													htmlFor="event"
													className={`cursor-pointer ${
														field.value === 'event'
															? 'text-foreground'
															: 'text-muted-foreground'
													}`}
												>
													{t('entities.music.playlist_for_events')}
												</Label>
											</div>
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						{/* Details Section - Show for both regular and event playlists */}
						<FormField
							control={form.control}
							name="event_details"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{selectedType === 'event'
											? t('entities.music.provide_details_of_the_event')
											: t('entities.music.provide_details_of_the_playlist')}
									</FormLabel>
									<FormControl>
										<Textarea
											placeholder={t('entities.music.add_any_important_detail')}
											className="min-h-[100px] bg-input border-border text-foreground"
											maxLength={5000}
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Separator className="bg-border" />

						{/* Insert References Section */}
						<div className="space-y-3">
							<Label className="text-sm font-medium text-foreground">
								{t('entities.music.insert_references')}
							</Label>
							<p className="text-sm text-muted-foreground">
								{t('entities.music.reference_description')}
							</p>

							{referenceLinks.map((link, index) => (
								<FormField
									key={index}
									control={form.control}
									name={
										`song_reference_${index + 1}` as keyof RequestCustomPlaylistRequestDTO
									}
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<Input
													placeholder={t('entities.music.reference_link')}
													className="bg-input border-border text-foreground"
													value={link}
													onChange={(e) => {
														handleReferenceLinkChange(index, e.target.value)
														field.onChange(e.target.value)
													}}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							))}

							<div className="flex flex-col w-full">
								<button
									type="button"
									onClick={handleAddReferenceLink}
									disabled={referenceLinks.length >= 5}
									className={`flex items-center gap-2 pb-1 w-full transition-colors ${
										referenceLinks.length >= 5
											? 'text-muted-foreground cursor-not-allowed opacity-50'
											: 'text-foreground hover:text-foreground/80 cursor-pointer'
									}`}
								>
									<Icon
										name="plus-sm"
										width="16px"
										height="16px"
										color="currentColor"
									/>
									<span>{t('entities.music.add_link')}</span>
								</button>
								<div className="w-[80px] h-px bg-gray-800 mt-0" />
							</div>
						</div>

						<Separator className="bg-border" />

						{/* Upload Spotify Playlist Section */}
						<div className="space-y-3">
							<Label className="text-sm font-medium text-foreground">
								{t('entities.music.upload_spotify_playlist')}
							</Label>
							<p className="text-sm text-muted-foreground">
								{t('entities.music.spotify_playlist_description')}
							</p>
							<FormField
								control={form.control}
								name="spotify_url"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<Input
												placeholder={t('entities.music.spotify_playlist_link')}
												className="bg-input border-border text-foreground"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<Separator />

						<DialogFooter className="flex gap-3">
							<Button
								type="button"
								variant="outline"
								onClick={onClose}
								className="rounded-full bg-transparent border-border text-foreground hover:bg-accent"
							>
								{t('actions.cancel')}
							</Button>
							<Button
								type="submit"
								variant="default"
								disabled={isPending}
								className="rounded-full"
							>
								{isPending ? t('actions.loading') : t('entities.music.request')}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	)
}

export default RequestCustomPlaylistModal
